import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { RegisterGroupMemberListItem } from './RegisterGroupMemberListItem'
import { RegisterGroupUnitListItem } from './RegisterGroupUnitListItem'
import { useModal } from '../../../hooks/useModal'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Option } from '../../common/Dropdown'
import { CreateUpdateRegisterGroupModal } from '../CreateUpdateRegisterGroupModal'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { IGroupModel } from '../../../interfaces/models/groups.interfaces'
import { deleteGroup } from '../../../api/groups'

interface RegisterGroupListItemBaseProps {
  group: IGroupModel
}

export const RegisterGroupListItemBase = ({
  group,
}: RegisterGroupListItemBaseProps): React.ReactNode => {
  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()
  const {
    show: showDeleteConfirmationModal,
    open: openDeleteConfirmationModal,
    close: closeDeleteConfirmationModal,
  } = useModal()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const { mutate: mutateDeleteGroup } = useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('registerGroup.flashMessage.deleteSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('registerGroup.flashMessage.deleteFailure')
      )
    },
  })

  const onDeleteGroup = async (): Promise<void> => {
    mutateDeleteGroup(group._id)
  }

  const dropdownOptions: Option[] = [
    {
      value: 'edit',
      label: translate('common.actions.edit'),
      onClick: openEditModal,
    },
    {
      value: 'remove',
      label: translate('common.actions.delete'),
      onClick: () => openDeleteConfirmationModal(),
      destructive: true,
    },
  ]

  return (
    <>
      {group.unitsList && group.unitsList.length > 0 ? (
        <RegisterGroupUnitListItem
          group={group}
          dropdownOptions={dropdownOptions}
        />
      ) : (
        <RegisterGroupMemberListItem
          group={group}
          dropdownOptions={dropdownOptions}
        />
      )}
      <CreateUpdateRegisterGroupModal
        show={showEditModal}
        close={closeEditModal}
        group={group}
      />
      <ConfirmationModal
        title={translate('registerGroup.confirmationDialog.deleteTitle')}
        description={translate(
          'registerGroup.confirmationDialog.deleteDescription'
        )}
        show={showDeleteConfirmationModal}
        close={closeDeleteConfirmationModal}
        onConfirm={onDeleteGroup}
        deleteMode
      />
    </>
  )
}
